/**
 * Uptime em três camadas (Renato 2026-09-20).
 *
 * Há TRÊS "boots" diferentes num player, e confundi-los custa diagnóstico.
 * Em 20/09 um `adb install` foi tratado como equivalente a um reload de
 * página num teste, e o teste inteiro foi invalidado: os dois zeram a página,
 * mas só um mata o PROCESSO — e a manutenção automática do IndexedDB do
 * Chromium roda dentro do processo do app. Processo morto = manutenção nunca
 * rodou. Um campo único de "último boot" esconderia exatamente isso.
 *
 *   página   — desde o último `location.reload()`. De graça.
 *   processo — desde o app subir. Deduzido por sessionStorage (abaixo).
 *   aparelho — desde ligar. Exige nativo; não resolvido aqui.
 *
 * O truque do processo: `sessionStorage` sobrevive ao reload da página e
 * morre com o processo/WebView. Gravando um carimbo na primeira carga e
 * conferindo se ele ainda está lá, dá pra distinguir os dois sem código
 * nativo. Se o storage não estiver disponível, devolve null em vez de mentir.
 */

const PROCESS_START_KEY = 'efflux_process_started_at';

export interface UptimeInfo {
  /** ms desde a última carga da página. */
  pageMs: number;
  /** ms desde o processo do app subir, ou null se indeterminável. */
  processMs: number | null;
  /** true se esta carga de página foi um reload dentro do mesmo processo. */
  reloadedInSameProcess: boolean;
}

/**
 * Marca o início do processo, se ainda não marcado. Idempotente — chamar no
 * boot do app, antes de qualquer leitura.
 */
export function markProcessStart(): void {
  try {
    if (!window.sessionStorage?.getItem(PROCESS_START_KEY)) {
      window.sessionStorage?.setItem(PROCESS_START_KEY, String(Date.now()));
    }
  } catch {
    /* sessionStorage indisponível — getUptime devolve processMs null */
  }
}

export function getUptime(): UptimeInfo {
  const pageMs = Math.round(performance.now());

  let inicio: number | null = null;
  try {
    const bruto = window.sessionStorage?.getItem(PROCESS_START_KEY);
    const n = bruto ? Number(bruto) : NaN;
    inicio = Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    inicio = null;
  }

  if (inicio === null) {
    return { pageMs, processMs: null, reloadedInSameProcess: false };
  }

  const processMs = Math.max(pageMs, Date.now() - inicio);
  // Margem de 2s: numa carga nova o carimbo acabou de ser escrito, então os
  // dois valores coincidem. Diferença real indica reload no mesmo processo.
  return {
    pageMs,
    processMs,
    reloadedInSameProcess: processMs - pageMs > 2000,
  };
}
